import React, { useCallback, useState } from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import { TextField, Autocomplete, CircularProgress } from "@mui/material";
import AnimatedErrorMessage from "../animatedErrorMessage";

export interface SelectOption {
  label: string;
  value: string | number;
}

export interface AsyncSelectFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  loadOptions: (inputValue: string) => Promise<SelectOption[]>;
  defaultOptions?: boolean | SelectOption[];
  isMulti?: boolean;
  isDisabled?: boolean;
  rules?: RegisterOptions;
  noOptionsMessage?: (obj: { inputValue: string }) => string;
}

// Shared sx styles for consistent appearance
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    fontSize: "15px",
    fontFamily: '"Inter", sans-serif',
    "& fieldset": {
      borderColor: "#E2E8F0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#CBD5E1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0078D4",
      borderWidth: "1px",
    },
    "&.Mui-error fieldset": {
      borderColor: "#ED4F9D",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "16px 14px",
    fontSize: "15px",
    fontFamily: '"Inter", sans-serif',
    color: "#334155",
    fontWeight: 500,
    letterSpacing: "-0.01em",
    "&::placeholder": {
      color: "#8A99AE",
      opacity: 1,
    },
  },
};

const BaseAsyncSelect: React.FC<AsyncSelectFieldProps> = ({
  name,
  label,
  placeholder = "Select…",
  loadOptions,
  defaultOptions = false,
  isMulti = false,
  isDisabled = false,
  rules = {},
  noOptionsMessage,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const fieldError = (errors as any)[name];
  const [options, setOptions] = useState<SelectOption[]>(
    Array.isArray(defaultOptions) ? defaultOptions : []
  );
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const handleInputChange = useCallback(
    async (_: any, newInputValue: string) => {
      setInputValue(newInputValue);
      if (newInputValue.length > 0) {
        setLoading(true);
        try {
          const results = await loadOptions(newInputValue);
          setOptions(results);
        } catch (error) {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      } else if (Array.isArray(defaultOptions)) {
        setOptions(defaultOptions);
      } else {
        setOptions([]);
      }
    },
    [loadOptions, defaultOptions]
  );

  // Load default options on focus if defaultOptions is true
  const handleOpen = useCallback(async () => {
    if (defaultOptions === true && options.length === 0) {
      setLoading(true);
      try {
        const results = await loadOptions("");
        setOptions(results);
      } catch (error) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }
  }, [defaultOptions, options.length, loadOptions]);

  return (
    <div>
      <Controller
        name={name}
        control={control}
        rules={rules}
        defaultValue={isMulti ? [] : null}
        render={({ field }) => (
          <Autocomplete
            {...field}
            multiple={isMulti}
            options={options}
            getOptionLabel={(option: SelectOption) => option?.label || ""}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            disabled={isDisabled}
            loading={loading}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onOpen={handleOpen}
            onChange={(_, newValue) => {
              field.onChange(newValue);
            }}
            noOptionsText={
              noOptionsMessage
                ? noOptionsMessage({ inputValue })
                : "No options"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                error={!!fieldError}
                sx={textFieldSx}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            sx={{
              "& .MuiAutocomplete-tag": {
                fontSize: "14px",
              },
            }}
          />
        )}
      />
      <AnimatedErrorMessage
        show={!!fieldError}
        message={fieldError?.message?.toString()}
        variant="form"
      />
    </div>
  );
};

export default BaseAsyncSelect;
